import React, { useEffect, useState } from 'react';
import { Alert, Box, Paper, Skeleton, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import DumbbellTrack, { PARTNERS_COLOR, YOU_COLOR } from './DumbbellTrack';
import TouchTooltip from '../../common/TouchTooltip';
import { DEPTH_FLOOR_NOTE, DEPTH_SOLD_OUT_AT_NOTE } from './constants';
import { dashboardAPI } from '../../../services/dashboard-api';
import { colourLabel, colourSwatch, daysInTen, depthTooltip, fmtInt } from '../../../utils/buyerInsightsFormat';

const GROUPS = {
  size: {
    more_depth: ['You have more depth', 'Partners were sold out of these sizes for more of the period than you.', 'None in this period.'],
    thinner: ["You're thinner", 'You were sold out of these sizes for more of the period than partners.', 'None — you are at least as deep as partners in every size.'],
  },
  colour: {
    more_depth: ['You have more depth', 'Partners were sold out of these colours for more of the period than you.', 'None in this period.'],
    thinner: ["You're thinner", 'You were sold out of these colours for more of the period than partners.', 'None — you are at least as deep as partners in every colour.'],
  },
};

const Swatch = ({ colour }) => (
  <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: colourSwatch(colour), border: '1px solid #cfd8dc', flexShrink: 0 }} />
);

// Every depth row and its scale header share one grid so labels, bars and numbers line up.
const gridSx = (isWide) => ({
  display: 'grid',
  gridTemplateColumns: isWide ? '136px minmax(100px, 1fr) max-content' : '96px minmax(80px, 1fr) max-content',
  columnGap: 1.5,
  alignItems: 'center',
});

const Key = ({ color, label }) => (
  <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

// The scale above a group's bars — direction first, like the click balance.
const ScaleHeader = ({ isWide }) => (
  <Box sx={{ ...gridSx(isWide), mt: 1, mb: 0.25 }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Key color={YOU_COLOR} label="You" />
      <Key color={PARTNERS_COLOR} label="Partners" />
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
        ← sold out on fewer days · sold out on more days →
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.disabled">never</Typography>
        <Typography variant="caption" color="text.disabled">every day</Typography>
      </Box>
    </Box>
    <Box />
  </Box>
);

const DepthRow = ({ kind, row, window, isWide }) => (
  <TouchTooltip title={depthTooltip(kind, row, window)}>
    <Box sx={{ ...gridSx(isWide), py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        {kind === 'colour' && <Swatch colour={row.label} />}
        <Typography variant="body2" noWrap>
          {kind === 'size' ? row.label : colourLabel(row.label)}
        </Typography>
      </Box>
      <DumbbellTrack you={row.you_pct} partners={row.partners_pct} width="100%" />
      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          {`Sold out ${daysInTen(row.you_pct)}`}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ whiteSpace: 'nowrap' }}>
          {`partners ${daysInTen(row.partners_pct)}`}
        </Typography>
      </Box>
    </Box>
  </TouchTooltip>
);

const unit = (kind, n) => (kind === 'size' ? (n === 1 ? 'size' : 'sizes') : n === 1 ? 'colour' : 'colours');

const Group = ({ kind, groupKey, rows, window, isWide }) => {
  const [title, description, empty] = GROUPS[kind][groupKey];
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: groupKey === 'thinner' ? 'warning.dark' : 'text.primary' }}>
        {title}
        {rows.length > 0 && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {`· ${fmtInt(rows.length)} ${unit(kind, rows.length)}`}
          </Typography>
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div">
        {description}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
          {empty}
        </Typography>
      ) : (
        <>
          <ScaleHeader isWide={isWide} />
          {rows.map((r) => (
            <DepthRow key={r.label} kind={kind} row={r} window={window} isWide={isWide} />
          ))}
        </>
      )}
    </Box>
  );
};

const Section = ({ kind, title, groups, window, isWide, children }) => (
  <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <TouchTooltip
        title={`On the days you and your partners both had the same styles online: how many days in 10 each ${kind === 'size' ? 'size' : 'colour'} was sold out, with you and with your partners. ${DEPTH_FLOOR_NOTE(kind === 'size' ? 'Sizes' : 'Colours')}`}
      >
        <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
      </TouchTooltip>
    </Box>
    <Group kind={kind} groupKey="thinner" rows={groups.thinner} window={window} isWide={isWide} />
    <Group kind={kind} groupKey="more_depth" rows={groups.more_depth} window={window} isWide={isWide} />
    {groups.not_compared > 0 && (
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
        {`${fmtInt(groups.not_compared)} ${unit(kind, groups.not_compared)} not compared (too few styles)`}
      </Typography>
    )}
    {children}
  </Paper>
);

/**
 * Expanded brand, part B (spec §1.4): By size, then By colour with Top colours you don't carry.
 * Fetched on expand (the per-product detail limiter queues Expand all).
 */
const BrandDepth = ({ brand, params, window, isWide }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    setDetail(null);
    setError('');
    dashboardAPI
      .getBuyerInsightsBrandDetail(brand, params, { signal: controller?.signal })
      .then((res) => {
        if (res?.success) setDetail(res.data);
        else setError(res?.error || 'Could not load the size and colour comparison');
      })
      .catch((e) => {
        if (e?.name !== 'AbortError') setError(e.message || 'Could not load the size and colour comparison');
      });
    return () => controller?.abort();
  }, [brand, paramsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>;
  if (!detail) return <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1, my: 1 }} />;

  const top = detail.top_colours_not_carried;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isWide ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
        gap: 2,
        pt: 1.5,
        alignItems: 'start',
      }}
    >
      {/* depth compares days online, not sell-outs: the Sold out at filter is ignored here (sold-out-at spec §3) */}
      {params?.sold_out_at && (
        <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
          {DEPTH_SOLD_OUT_AT_NOTE}
        </Typography>
      )}
      <Section kind="size" title="By size" groups={detail.by_size} window={window} isWide={isWide} />
      <Section kind="colour" title="By colour" groups={detail.by_colour} window={window} isWide={isWide}>
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Top colours you don&apos;t carry
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            Lead partners&apos; clicks for this brand
          </Typography>
          {top.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
              {`None — you carry all of ${brand}'s leading colours.`}
            </Typography>
          ) : (
            top.map((t) => (
              <Box key={t.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5 }}>
                <Swatch colour={t.label} />
                <Typography variant="body2">{colourLabel(t.label)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {`#${t.rank} · ${t.share_pct}% of partner clicks`}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      </Section>
    </Box>
  );
};

export default BrandDepth;
